import React from "react";
import Loader from "../Loader/Loader";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";
export type ButtonType = "button" | "submit" | "reset";
export type IconPosition = "left" | "right";

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  type?: ButtonType;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: IconPosition;
  fullWidth?: boolean;
  className?: string;
  children?: React.ReactNode;
}

function Button({
  variant = "primary",
  size = "md",
  type = "button",
  disabled = false,
  loading = false,
  icon,
  iconPosition = "left",
  fullWidth = false,
  onClick,
  className,
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    "ui-button",
    `ui-button--${variant}`,
    `ui-button--${size}`,
    fullWidth ? "ui-button--full-width" : null,
    loading ? "ui-button--loading" : null,
    className || null,
  ]
    .filter(Boolean)
    .join(" ");

  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      onClick={isDisabled ? undefined : onClick}
      {...rest}
    >
      {loading ? (
        <Loader inline className="ui-button__loader" />
      ) : (
        <>
          {icon && iconPosition === "left" ? (
            <span className="ui-button__icon ui-button__icon--left">{icon}</span>
          ) : null}
          <span className="ui-button__label">{children}</span>
          {icon && iconPosition === "right" ? (
            <span className="ui-button__icon ui-button__icon--right">{icon}</span>
          ) : null}
        </>
      )}
    </button>
  );
}

export default Button;
